/** @ken/module-journal-entries — frontend entry point.
 *
 * Mirrors @ken/module-invoicing shape. Journal entries are accountant-
 * posted manual debit/credit adjustments. Source-canonical fields +
 * custom_field_values render via the unified tab system (canonical
 * layout + field definitions, merged client-side per ADR-071).
 *
 * 0.2.0 — story-page migration (ADR-0081): the in-list Sheet is retired;
 * the detail is now a routed story page at journal-entries/$id.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { JournalEntryStorySearch } from "./routes/story/journal-entry-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadJournalEntryStoryTabs = () => import("./routes/story/journal-entry-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const JournalEntryCreatePage = lazyScreen(
  () => import("./routes/journal-entry-create"),
  "JournalEntryCreatePage",
);
const JournalEntryListPage = lazyScreen(
  () => import("./routes/journal-entry-list"),
  "JournalEntryListPage",
);
const JournalEntryStoryDetail = lazyScreen(
  () => import("./routes/story/journal-entry-story-detail"),
  "JournalEntryStoryDetail",
);
const JournalEntryActivityRoute = lazyScreen(
  loadJournalEntryStoryTabs,
  "JournalEntryActivityRoute",
);
const JournalEntryDistributionRoute = lazyScreen(
  loadJournalEntryStoryTabs,
  "JournalEntryDistributionRoute",
);
const JournalEntryNotesRoute = lazyScreen(
  loadJournalEntryStoryTabs,
  "JournalEntryNotesRoute",
);
const JournalEntryOverviewRoute = lazyScreen(
  loadJournalEntryStoryTabs,
  "JournalEntryOverviewRoute",
);
const JournalEntryRelatedRoute = lazyScreen(
  loadJournalEntryStoryTabs,
  "JournalEntryRelatedRoute",
);
export type { JournalEntryStorySearch } from "./routes/story/journal-entry-story-detail";

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "journal-entries",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3).
    validateSearch: (raw: Record<string, unknown>): JournalEntryListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: JournalEntryListPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "journal-entries/new",
    component: JournalEntryCreatePage,
  });

  // ── Story-page detail (ADR-0081): parent journal-entries/$id shell +
  //    routed tab children + an index redirect to …/overview. ────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "journal-entries/$id",
    validateSearch: (
      raw: Record<string, unknown>,
    ): JournalEntryStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: JournalEntryStoryDetail,
  });

  // Index redirect journal-entries/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/journal-entries/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: JournalEntryOverviewRoute,
  });
  const distributionRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "distribution",
    component: JournalEntryDistributionRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: JournalEntryRelatedRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: JournalEntryNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: JournalEntryActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      distributionRoute,
      relatedRoute,
      activityRoute,
      notesRoute,
    ],
  );

  return [listRoute, createRouteEntry, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface JournalEntryListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
